import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, FileDown, Receipt, Loader2 } from "lucide-react";

const fmt = (s) => (s ? new Date(s).toLocaleString("ar-SA-u-nu-latn", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function MyInvoices() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/my/invoices").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const download = async (p) => {
    setBusy(p.id);
    try {
      const res = await api.get(`/payments/${p.id}/invoice`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${p.invoice_no || "invoice"}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      if (!p.invoice_no) load();
    } catch (e) { toast.error("تعذّر تنزيل الفاتورة"); }
    setBusy("");
  };

  return (
    <Shell title="فواتيري" subtitle="كل فواتير مدفوعاتك في مكان واحد، تنزّلها متى ما احتجتها" icon={Receipt}
      actions={<Button variant="outline" className="rounded-full gap-2" onClick={() => navigate("/app")} data-testid="invoices-back-btn"><ArrowRight className="w-4 h-4" /> رجوع</Button>}>
      <Card className="divide-y divide-border" data-testid="my-invoices">
        {rows === null && <div className="p-8 text-center text-sm text-muted-foreground">جارٍ التحميل...</div>}
        {rows?.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="invoices-empty">لا توجد فواتير بعد — تظهر هنا بعد كل عملية دفع ناجحة</div>
        )}
        {rows?.map((p) => (
          <div key={p.id} className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`invoice-row-${p.id}`}>
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Receipt className="w-5 h-5 text-primary" /></div>
              <div className="min-w-0">
                <div className="font-semibold">{p.kind === "repair" ? "تسوية إصلاح المركبة" : "سحب المركبة"}{p.party_name && <span className="text-muted-foreground font-normal"> · {p.party_name}</span>}</div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                  {p.invoice_no && <span>فاتورة <bdi dir="ltr">{p.invoice_no}</bdi></span>}
                  <bdi>{fmt(p.paid_at)}</bdi><span>{p.provider}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-lg font-extrabold whitespace-nowrap" data-testid={`invoice-amount-${p.id}`}>{Number(p.amount || 0).toFixed(2)} ر.س</div>
              <Button size="sm" className="rounded-full gap-2" disabled={busy === p.id} onClick={() => download(p)} data-testid={`invoice-download-${p.id}`}>
                {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} تنزيل PDF
              </Button>
            </div>
          </div>
        ))}
      </Card>
    </Shell>
  );
}
